import { FC } from "react";
import classNames from "classnames";

import { ShowWhen } from "@/components/common";

export type RadioButtonProps = {
  id: string;
  value?: string | number;
  label?: string;
  additionalLabel?: string;
  price?: string;
  name?: string;
  className?: string;
  selected?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  squared?: boolean;
  icon?: any;
  children?: React.ReactNode;
  primaryColor?: boolean;
};

const CustomRadioButton: FC<RadioButtonProps> = ({
  id,
  value,
  label,
  additionalLabel,
  price,
  name,
  selected,
  onChange,
  className,
  disabled,
  squared,
  icon,
  children,
  primaryColor,
}) => {
  return (
    <ShowWhen
      condition={!squared}
      elseRender={
        <div
          className={classNames(
            "duration-400 rounded-[10px] border-[1.5px] bg-white transition-all",
            selected ? "border-[#5dd826]/50" : "border-[#dedede]/50",
            className,
          )}
        >
          <label className="flex cursor-pointer p-[1.125rem]" htmlFor={id}>
            <div className="flex w-[50%] items-center gap-[0.625rem]">
              <div className="flex h-[46px] w-[46px] items-center justify-center rounded-full bg-[#f9f9f9]">
                {icon}
              </div>
              <div className="flex flex-col gap-[4px]">
                <h5 className="text-sm font-semibold text-[#121212]">
                  {label}
                </h5>
                <ShowWhen condition={!!additionalLabel}>
                  <p className="text-xs font-normal text-[#808080]">
                    {additionalLabel}
                  </p>
                </ShowWhen>
              </div>
            </div>
            <div className="flex w-[50%] items-center justify-between">
              <p className="text-sm font-normal text-[#121212]">
                {price ? `$ ${price}` : "-"}
              </p>
              <div className="flex items-center gap-[0.875rem]">
                <div className="h-[25px] w-[1px] bg-[#f5f5f5]" />
                <div className="relative h-[30px] w-[30px]">
                  <input
                    className={classNames(
                      "absolute left-[50%] top-[50%] h-5 w-5 translate-x-[-50%] translate-y-[-50%] cursor-pointer appearance-none rounded-full border-[1.5px] border-[#B0B0B0]",
                      {
                        "mr-2": label,
                        "checked:border-[#5DD927]": selected,
                      },
                    )}
                    name={name}
                    id={id}
                    checked={selected}
                    value={value}
                    disabled={disabled}
                    onChange={onChange}
                    type="checkbox"
                  />
                  <div
                    className={classNames(
                      "z-5 absolute left-[50%] top-[50%] h-3 w-3 translate-x-[-50%] translate-y-[-50%] rounded-full",
                      {
                        "bg-[#5DD927]": selected,
                      },
                    )}
                  />
                </div>
              </div>
            </div>
          </label>
          <ShowWhen condition={!!children}>
            <div
              className={classNames(
                selected
                  ? "h-fit p-[1.125rem] pt-0 opacity-100"
                  : "h-0 opacity-0",
                "duration-400 transition-all",
              )}
            >
              {children}
            </div>
          </ShowWhen>
        </div>
      }
    >
      <div
        className={classNames("relative flex h-5 w-5", className, {
          "flex-row": label,
        })}
      >
        <input
          className={classNames(
            "relative z-10 h-full w-full cursor-pointer rounded-full border-[1.5px] opacity-0",
            {
              "mr-2": label,
              "checked:border-[#5DD927]": selected && !primaryColor,
              "primary-border": selected && primaryColor,
            },
          )}
          name={name}
          id={id}
          checked={selected}
          value={value}
          disabled={disabled}
          onChange={onChange}
          type="checkbox"
        />
        <div
          className={classNames(
            "absolute left-0 top-0 z-[5] flex h-full w-full items-center justify-center rounded-full border-[2px]",
            {
              "mr-2": label,
              "border-[#8F8F8F]": !selected,
              "bg-[#5DD927]": selected && !primaryColor,
              "primary-border bg-white": selected && primaryColor,
            },
          )}
        >
          <div
            className={classNames("h-3 w-3 rounded-full", {
              "bg-[#5DD927]": selected && !primaryColor,
              "primary-bg": selected && primaryColor,
            })}
          />
        </div>
        <ShowWhen condition={!!label} elseRender={null}>
          <label
            htmlFor={id}
            className="ml-2 flex cursor-pointer items-center text-xs text-zinc-800"
          >
            {label}
          </label>
        </ShowWhen>
      </div>
    </ShowWhen>
  );
};

export { CustomRadioButton };
